<script>
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
export default {
  components: {
    BaseSettingsHeader,
  },
  setup() {
    const { accountId } = useAccount();
    const { formatMessage } = useMessageFormatter();

    return {
      accountId,
      formatMessage,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
    }),
    locale() {
      return this.$i18n?.locale || navigator.language || 'en-US';
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    customAttributes() {
      return this.currentAccount.custom_attributes || {};
    },
    subscription() {
      return this.currentAccount.subscription || {};
    },
    planName() {
      return this.subscription.plan_name || this.customAttributes.plan_name || '';
    },
    subscribedQuantity() {
      const quantity =
        this.subscription.subscribed_quantity ||
        this.customAttributes.subscribed_quantity ||
        0;
      return Number(quantity) || 0;
    },
    subscriptionStatusRaw() {
      const status =
        this.subscription.status || this.customAttributes.subscription_status || '';
      return status.toString().toLowerCase();
    },
    isTrial() {
      if (typeof this.subscription.is_trial === 'boolean') {
        return this.subscription.is_trial;
      }
      return this.subscriptionStatusRaw === 'trial';
    },
    isActive() {
      if (typeof this.subscription.is_active === 'boolean') {
        return this.subscription.is_active;
      }
      return this.subscriptionStatusRaw === 'active';
    },
    isExpired() {
      if (typeof this.subscription.is_expired === 'boolean') {
        return this.subscription.is_expired;
      }
      return this.subscriptionStatusRaw === 'expired';
    },
    isCancelled() {
      if (typeof this.subscription.is_cancelled === 'boolean') {
        return this.subscription.is_cancelled;
      }
      return this.subscriptionStatusRaw === 'cancelled';
    },
    isUnlimited() {
      const cycle = this.subscription.billing_cycle || this.subscription.price?.interval;
      return cycle === 'unlimited';
    },
    headerDescription() {
      return this.$t('BILLING_SETTINGS.PAGE_HEADER.DESCRIPTION');
    },
    planSummaryText() {
      return (
        this.subscription.plan_description ||
        this.$t('BILLING_SETTINGS.SUMMARY.PLAN_DESCRIPTION_FALLBACK')
      );
    },
    planNoteHtml() {
      if (!this.planName) return null;
      return this.formatMessage(
        this.$t('BILLING_SETTINGS.CURRENT_PLAN.PLAN_NOTE', {
          plan: this.planName,
          quantity: this.subscribedQuantity,
        })
      );
    },
    priceInfo() {
      return this.subscription.price || null;
    },
    pricePerPeriod() {
      const price = this.priceInfo;
      if (!price || price.unit_amount_cents === null || price.unit_amount_cents === undefined) {
        return this.$t('BILLING_SETTINGS.PRICE.FREE');
      }
      if (price.unit_amount_cents === 0) {
        return this.$t('BILLING_SETTINGS.PRICE.FREE');
      }
      const formatted = this.formatCurrency(price.unit_amount_cents / 100, price.currency);
      const interval = price.interval || this.subscription.billing_cycle;
      if (interval && interval !== 'unlimited') {
        const intervalLabel = this.billingCycleLabelForInterval(interval);
        return `${formatted} / ${intervalLabel.toLowerCase()}`;
      }
      return formatted;
    },
    amountDue() {
      if (this.isTrial) {
        return this.$t('BILLING_SETTINGS.BILLING_DETAILS.AMOUNT_DUE_FREE');
      }
      const price = this.priceInfo;
      if (!price || !price.unit_amount_cents) {
        return this.$t('BILLING_SETTINGS.PRICE.FREE');
      }
      return this.formatCurrency(price.unit_amount_cents / 100, price.currency);
    },
    subscriptionEndDate() {
      return this.subscription.end_date || this.customAttributes.subscription_ends_on || null;
    },
    trialEndDate() {
      return this.subscription.trial_ends_at || null;
    },
    nextBillingDateFormatted() {
      return this.formatDate(this.subscription.next_billing_date);
    },
    startDateFormatted() {
      return this.formatDate(this.subscription.start_date);
    },
    trialEndFormatted() {
      return this.formatDate(this.trialEndDate);
    },
    expiryDateFormatted() {
      if (this.isUnlimited) {
        return this.$t('BILLING_SETTINGS.STATUS.TIME_REMAINING_UNLIMITED');
      }
      return this.formatDate(this.subscriptionEndDate);
    },
    daysUntilExpiry() {
      if (this.isTrial) {
        const trialDays = this.subscription.days_until_trial_expiry;
        if (Number.isInteger(trialDays)) {
          return trialDays;
        }
      }
      const expiryDays = this.subscription.days_until_expiry;
      if (Number.isInteger(expiryDays)) {
        return expiryDays;
      }
      const endDate = this.subscriptionEndDate;
      if (!endDate) return null;
      const parsed = new Date(endDate);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      parsed.setHours(0, 0, 0, 0);
      const diff = Math.ceil((parsed - today) / (1000 * 60 * 60 * 24));
      return diff;
    },
    timeRemaining() {
      if (this.isUnlimited) {
        return this.$t('BILLING_SETTINGS.STATUS.TIME_REMAINING_UNLIMITED');
      }
      if (this.daysUntilExpiry === null) {
        return this.$t('BILLING_SETTINGS.STATUS.TIME_REMAINING_UNKNOWN');
      }
      if (this.daysUntilExpiry <= 0) {
        return this.$t('BILLING_SETTINGS.STATUS.TIME_REMAINING_EXPIRED');
      }
      if (this.daysUntilExpiry === 1) {
        return this.$t('BILLING_SETTINGS.STATUS.TIME_REMAINING_DAY', { count: 1 });
      }
      return this.$t('BILLING_SETTINGS.STATUS.TIME_REMAINING_DAYS', {
        count: this.daysUntilExpiry,
      });
    },
    statusBadgeClass() {
      if (this.isTrial) {
        return 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200';
      }
      if (this.isExpired || this.isCancelled) {
        return 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200';
      }
      return 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200';
    },
    statusText() {
      const mapping = {
        trial: this.$t('BILLING_SETTINGS.STATUS.TRIAL'),
        active: this.$t('BILLING_SETTINGS.STATUS.ACTIVE'),
        expired: this.$t('BILLING_SETTINGS.STATUS.EXPIRED'),
        cancelled: this.$t('BILLING_SETTINGS.STATUS.CANCELLED'),
      };
      return mapping[this.subscriptionStatusRaw] || this.subscriptionStatusRaw || '—';
    },
    statusDescription() {
      if (this.isTrial) {
        return this.$t('BILLING_SETTINGS.STATUS.DESCRIPTION_TRIAL');
      }
      if (this.isExpired) {
        return this.$t('BILLING_SETTINGS.STATUS.DESCRIPTION_EXPIRED');
      }
      if (this.isCancelled) {
        return this.$t('BILLING_SETTINGS.STATUS.DESCRIPTION_CANCELLED');
      }
      return this.$t('BILLING_SETTINGS.STATUS.DESCRIPTION_ACTIVE');
    },
    planStats() {
      return [
        {
          label: this.$t('BILLING_SETTINGS.SUMMARY.PRICE_PER_PERIOD'),
          value: this.pricePerPeriod,
        },
        {
          label: this.$t('BILLING_SETTINGS.SUMMARY.BILLING_CYCLE'),
          value: this.billingCycleLabel,
        },
        {
          label: this.$t('BILLING_SETTINGS.SUMMARY.SUBSCRIBED_LICENSES'),
          value: this.subscribedQuantity || this.$t('BILLING_SETTINGS.PRICE.FREE'),
        },
      ];
    },
    billingCycleLabel() {
      const cycle = this.subscription.billing_cycle || this.subscription.price?.interval;
      return this.billingCycleLabelForInterval(cycle);
    },
    timelineEntries() {
      const entries = [
        {
          label: this.$t('BILLING_SETTINGS.SUMMARY.START_DATE'),
          value: this.startDateFormatted,
        },
        {
          label: this.$t('BILLING_SETTINGS.SUMMARY.NEXT_BILLING_DATE'),
          value: this.nextBillingDateFormatted,
        },
      ];
      if (this.trialEndFormatted) {
        entries.splice(1, 0, {
          label: this.$t('BILLING_SETTINGS.SUMMARY.TRIAL_END_DATE'),
          value: this.trialEndFormatted,
        });
      }
      if (!this.isUnlimited) {
        entries.push({
          label: this.$t('BILLING_SETTINGS.SUMMARY.EXPIRY_DATE'),
          value: this.expiryDateFormatted,
        });
      }
      return entries;
    },
    quickActions() {
      return [
        {
          key: 'manage',
          title: this.$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE'),
          description: this.$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION'),
          cta: this.$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT'),
          icon: 'i-lucide-credit-card',
          handler: () => this.onClickBillingPortal(),
        },
        {
          key: 'usage',
          title: this.$t('BILLING_SETTINGS.CTA.GO_TO_USAGE'),
          description: this.$t('USAGE_SETTINGS.PAGE_DESCRIPTION'),
          cta: this.$t('BILLING_SETTINGS.CTA.GO_TO_USAGE'),
          icon: 'i-lucide-activity',
          handler: () => this.goToUsage(),
        },
        {
          key: 'support',
          title: this.$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE'),
          description: this.$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION'),
          cta: this.$t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT'),
          icon: 'chat-multiple',
          handler: () => this.onToggleChatWindow(),
        },
      ];
    },
  },
  mounted() {
    if (!this.planName) {
      this.fetchAccountDetails();
    }
  },
  methods: {
    async fetchAccountDetails() {
      this.$store.dispatch('accounts/subscription');
    },
    onClickBillingPortal() {
      this.$store.dispatch('accounts/checkout');
    },
    onToggleChatWindow() {
      if (window.$hichat) {
        window.$hichat.toggle();
      }
    },
    goToUsage() {
      this.$router.push({
        name: 'settings_usage_index',
        params: { accountId: this.accountId },
      });
    },
    formatCurrency(amount, currency) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    },
    formatDate(dateString) {
      if (!dateString) {
        return this.$t('BILLING_SETTINGS.SUMMARY.NO_DATE');
      }
      const parsed = new Date(dateString);
      if (Number.isNaN(parsed.getTime())) {
        return this.$t('BILLING_SETTINGS.SUMMARY.NO_DATE');
      }
      try {
        return new Intl.DateTimeFormat(this.locale, { dateStyle: 'medium' }).format(parsed);
      } catch (error) {
        return parsed.toISOString().split('T')[0];
      }
    },
    billingCycleLabelForInterval(interval) {
      const mapping = {
        monthly: this.$t('BILLING_SETTINGS.BILLING_CYCLE_LABELS.MONTHLY'),
        yearly: this.$t('BILLING_SETTINGS.BILLING_CYCLE_LABELS.YEARLY'),
        unlimited: this.$t('BILLING_SETTINGS.BILLING_CYCLE_LABELS.UNLIMITED'),
      };
      if (!interval) {
        return this.$t('BILLING_SETTINGS.SUMMARY.NO_DATE');
      }
      return mapping[interval] || interval;
    },
  },
};
</script>

<template>
  <div>
    <woot-loading-state v-if="uiFlags.isFetchingItem" />
    <div v-else class="space-y-6">
      <BaseSettingsHeader
        :description="$t('BILLING_SETTINGS.PAGE_HEADER.DESCRIPTION')"
        feature-name="billing"
      />

      <section
        v-if="!planName"
        class="billing-card text-sm text-slate-600 dark:text-slate-300"
      >
        {{ $t('BILLING_SETTINGS.NO_BILLING_USER') }}
      </section>

      <template v-else>
        <div class="grid gap-6 lg:grid-cols-12">
          <section class="billing-card lg:col-span-8">
            <header class="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {{ planName || $t('BILLING_SETTINGS.SUMMARY.CURRENT_PLAN_FALLBACK') }}
                </h2>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {{ planSummaryText }}
                </p>
              </div>
              <span :class="['rounded-full px-3 py-1 text-xs font-semibold self-start', statusBadgeClass]">
                {{ statusText }}
              </span>
            </header>
            <div
              v-if="planNoteHtml"
              class="mt-3 text-sm text-slate-600 dark:text-slate-300"
              v-dompurify-html="planNoteHtml"
            />
            <dl class="mt-4 grid gap-4 sm:grid-cols-3">
              <div
                v-for="stat in planStats"
                :key="stat.label"
                class="billing-stat"
              >
                <dt>{{ stat.label }}</dt>
                <dd>{{ stat.value }}</dd>
              </div>
            </dl>
          </section>

          <section class="billing-card lg:col-span-4">
            <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
              {{ $t('BILLING_SETTINGS.STATUS.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {{ statusDescription }}
            </p>
            <ul class="mt-4 space-y-3 text-sm">
              <li class="flex items-center justify-between">
                <span class="text-slate-500 dark:text-slate-400">
                  {{ $t('BILLING_SETTINGS.BILLING_DETAILS.AMOUNT_DUE') }}
                </span>
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ amountDue }}
                </span>
              </li>
              <li class="flex items-center justify-between">
                <span class="text-slate-500 dark:text-slate-400">
                  {{ $t('BILLING_SETTINGS.STATUS.TIME_REMAINING_LABEL') }}
                </span>
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ timeRemaining }}
                </span>
              </li>
              <li class="flex items-center justify-between">
                <span class="text-slate-500 dark:text-slate-400">
                  {{ $t('BILLING_SETTINGS.SUMMARY.NEXT_BILLING_DATE') }}
                </span>
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ nextBillingDateFormatted }}
                </span>
              </li>
              <li v-if="trialEndFormatted" class="flex items-center justify-between">
                <span class="text-slate-500 dark:text-slate-400">
                  {{ $t('BILLING_SETTINGS.SUMMARY.TRIAL_END_DATE') }}
                </span>
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ trialEndFormatted }}
                </span>
              </li>
              <li v-if="!isUnlimited" class="flex items-center justify-between">
                <span class="text-slate-500 dark:text-slate-400">
                  {{ $t('BILLING_SETTINGS.SUMMARY.EXPIRY_DATE') }}
                </span>
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ expiryDateFormatted }}
                </span>
              </li>
            </ul>
          </section>
        </div>

        <section class="billing-card">
          <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('BILLING_SETTINGS.SUMMARY.TIMELINE_TITLE') }}
          </h3>
          <ul class="mt-4 divide-y divide-slate-200 dark:divide-slate-700">
            <li
              v-for="item in timelineEntries"
              :key="item.label"
              class="flex items-center justify-between py-3 text-sm"
            >
              <span class="text-slate-500 dark:text-slate-400">{{ item.label }}</span>
              <span class="font-medium text-slate-900 dark:text-slate-100">
                {{ item.value }}
              </span>
            </li>
          </ul>
        </section>

        <section class="billing-card">
          <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
                {{ $t('BILLING_SETTINGS.ACTIONS.TITLE') }}
              </h3>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ $t('BILLING_SETTINGS.ACTIONS.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="mt-4 grid gap-4 md:grid-cols-3">
            <article
              v-for="action in quickActions"
              :key="action.key"
              class="billing-action"
            >
              <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {{ action.title }}
              </h4>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ action.description }}
              </p>
              <woot-button
                variant="smooth"
                size="small"
                :icon="action.icon"
                class="mt-auto"
                @click="action.handler()"
              >
                {{ action.cta }}
              </woot-button>
            </article>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.billing-card {
  @apply rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800;
}

.billing-stat {
  @apply rounded-lg bg-slate-50 p-4 dark:bg-slate-700/40;

  dt {
    @apply text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300;
  }

  dd {
    @apply mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100;
  }
}

.billing-action {
  @apply flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700/40;
}
</style>
